/**
 * Pure, network-free helpers for LIVE Poll-block voting (Bucket-D Wave-D2).
 * Kept separate from the React component so the vote math + session bookkeeping
 * are unit-testable without craft.js / jsdom / a mocked axios.
 */

/** localStorage key for the UX "already voted this session" marker. The server
 * is the source of truth for dedup; this only decides whether a returning voter
 * sees the form or the standings. */
export function votedStorageKey(wheelId, blockId) {
  return `iyuga_poll_voted_${wheelId}_${blockId}`;
}

/** Normalise a `{ tallies, total }` API payload into a safe shape. Recomputes
 * total from the tallies when the server omits it, and never returns NaN. */
export function parseResults(data) {
  const tallies = data && typeof data.tallies === "object" && data.tallies ? data.tallies : {};
  let total = data && typeof data.total === "number" ? data.total : NaN;
  if (!Number.isFinite(total)) {
    total = Object.values(tallies).reduce((a, b) => a + (Number(b) || 0), 0);
  }
  return { tallies, total: Math.max(0, total) };
}

/** Whole-percent share of the vote for one option id (0 when there are no votes). */
export function percentFor(tallies, total, optionId) {
  if (!total || total <= 0) return 0;
  const count = (tallies && Number(tallies[optionId])) || 0;
  const pct = Math.round((count / total) * 100);
  return Math.max(0, Math.min(100, pct));
}

/** The vote request body for the shared BE endpoint. De-dupes + drops empty ids
 * and, for single-choice, keeps only the first selection (the server replaces a
 * prior single vote anyway; this keeps the payload honest). */
export function buildVotePayload(selectedIds, allowMultiple) {
  const ids = [...new Set((selectedIds || []).filter(Boolean))];
  const optionIds = allowMultiple ? ids : ids.slice(0, 1);
  return { optionIds, allowMultiple: !!allowMultiple };
}

/** Safe localStorage read — treats private-mode / disabled storage as "no marker". */
export function hasVotedMarker(storage, wheelId, blockId) {
  try {
    return !!(storage && storage.getItem(votedStorageKey(wheelId, blockId)));
  } catch (e) {
    return false;
  }
}

/** Safe localStorage write — a storage failure is non-fatal. */
export function setVotedMarker(storage, wheelId, blockId) {
  try {
    if (storage) storage.setItem(votedStorageKey(wheelId, blockId), "1");
  } catch (e) {
    /* ignore */
  }
}
